import { useEffect, useState } from "react"
import { getInvoiceSummary } from "../../../services/Invoice";
import moment from "moment";
import { getPositions } from "../../../services/Tires";
import { formatUSPhone } from "../../../utils/phoneFormatter";
import { getCustomerServices } from "../../../services/Services";

type InvoiceProps = {
    primaryKey: null | number;
    setPleaseWaitPopup: (value: boolean) => void;
    invoiceId: null | number;
    appointmentData: any;
    positions: any[];
    allServices: any[];
    goToNextPage: () => void
}

export const Invoice = ({ primaryKey, setPleaseWaitPopup, invoiceId, appointmentData, positions, allServices, goToNextPage }: InvoiceProps) => {
    const [invoiceData, setInvoiceData] = useState<any>({});
    const [positionsNames, setPositionsNames] = useState<any[]>([]);
    const [servicesOptions, setServicesOptions] = useState<any[]>([]);

    useEffect(() => {
        if (primaryKey && primaryKey > 0 && invoiceId && invoiceId > 0) {
            setPleaseWaitPopup(true);
            getInvoiceSummary(primaryKey)
                .then((data) => {
                    setInvoiceData(data);
                    setPleaseWaitPopup(false);
                })
                .catch((error) => {
                    console.error(error)
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primaryKey, invoiceId])

    useEffect(() => {
        getPositions("TirePosition")
            .then((data) => {
                setPositionsNames(data.reverse());
            }).catch((error) => {
                console.error(error);
                setPositionsNames([]);
            })
    }, [])

    useEffect(() => {
        getCustomerServices()
            .then((data) => {
                setServicesOptions(data?.reverse())
            }).catch((error) => {
                setServicesOptions([]);
                console.error(error);
            })
    }, [])

    return (
        <div className="w-full mt-2">
            <div className={`w-full pb-4`}>
                <div className="flex">
                    <div className="mt-2 p-2 w-[50%]">

                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-2">
                            <div>
                                <span className="text-sm font-semibold text-gray-800">
                                    Invoice #{invoiceData?.invoiceID}
                                </span>
                                {invoiceData?.invoiceDate && (
                                    <p className="text-sm text-gray-500">
                                        Date: {moment(new Date(invoiceData?.invoiceDate?.split("T")[0])).format("dddd, MMM DD, YYYY")}
                                    </p>
                                )}
                            </div>

                            {invoiceData?.status && (
                                <span
                                    className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold
                                            ${invoiceData?.status === "Paid"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                        }`}
                                >
                                    {invoiceData?.status}
                                </span>
                            )}
                        </div>

                        {/* Customer & Vehicle */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2 border-b pb-2">
                            {invoiceData?.customer && (
                                <div className="border rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        Customer
                                    </h3>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <p className="font-medium text-gray-900">{invoiceData?.customer.name}</p>
                                        {invoiceData?.customer.phone && <p>{formatUSPhone(invoiceData.customer.phone)}</p>}
                                        {invoiceData?.customer.email && <p>{invoiceData?.customer.email}</p>}
                                    </div>
                                </div>
                            )}

                            {invoiceData?.vehicle && (
                                <div className="border rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        Vehicle
                                    </h3>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <p className="font-medium text-gray-900">
                                            {invoiceData?.vehicle.make} {invoiceData?.vehicle.model}
                                        </p>
                                        {invoiceData?.vehicle.year && <p>Year: {invoiceData?.vehicle.year}</p>}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Location */}
                        {invoiceData?.location && (
                            <div className="border-b pb-2">
                                <div className="border rounded-lg p-4 mt-2">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        Service Location
                                    </h3>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <p className="font-medium text-gray-900">{invoiceData?.location.name}</p>
                                        {invoiceData?.location.phone && <p>{formatUSPhone(invoiceData.location.phone)}</p>}
                                        {invoiceData?.location.address && <p>{invoiceData?.location.address}</p>}
                                    </div>
                                </div>
                            </div>

                        )}


                        {(appointmentData !== null) && (
                            <div className="border-b pb-2">
                                <div className="border rounded-lg p-4 mt-2">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        Service Details
                                    </h3>
                                    <div className="text-sm text-gray-600 space-y-3">
                                        {
                                            appointmentData?.vehicleTrim !== null && (
                                                <p className="border-b pb-2 font-semibold text-gray-700">Trim: <span className="text-gray-700 font-medium">{appointmentData?.vehicleTrim?.trimName}</span></p>
                                            )
                                        }

                                        {
                                            appointmentData?.tireFitment !== null && (
                                                <div className="mt-4 border-b pb-2">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                                        {appointmentData?.rearTireFitment ? "Front Tire Details" : "Tire Details"}
                                                    </h3>
                                                    <div className="overflow-x-auto border rounded-lg">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50 text-gray-600">
                                                                <tr>
                                                                    <th className="text-left px-4 py-3">Width</th>
                                                                    <th className="text-left px-4 py-3">Ratio</th>
                                                                    <th className="text-left px-4 py-3">Diameter</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr className="border-t">
                                                                    <td className="px-4 py-3">{appointmentData?.tireFitment?.metricWidth}</td>
                                                                    <td className="px-4 py-3">{appointmentData?.tireFitment?.aspectRatio}</td>
                                                                    <td className="px-4 py-3">{appointmentData?.tireFitment?.diameter}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                </div>

                                            )
                                        }

                                        {
                                            appointmentData?.rearTireFitment !== null && (
                                                <div className="mt-4 border-b pb-2">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                                        Rear Tire Details
                                                    </h3>
                                                    <div className="overflow-x-auto border rounded-lg">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50 text-gray-600">
                                                                <tr>
                                                                    <th className="text-left px-4 py-3">Width</th>
                                                                    <th className="text-left px-4 py-3">Ratio</th>
                                                                    <th className="text-left px-4 py-3">Diameter</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr className="border-t">
                                                                    <td className="px-4 py-3">{appointmentData?.rearTireFitment?.metricWidth}</td>
                                                                    <td className="px-4 py-3">{appointmentData?.rearTireFitment?.aspectRatio}</td>
                                                                    <td className="px-4 py-3">{appointmentData?.rearTireFitment?.diameter}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                </div>
                                            )
                                        }




                                        {
                                            allServices.length > 0 && (
                                                <div className="mt-4 border-b pb-2">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                                        Tire Count
                                                    </h3>
                                                    <div className="overflow-x-auto border rounded-lg">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50 text-gray-600">
                                                                <tr>
                                                                    <th className="text-left px-4 py-3">Service Type</th>
                                                                    <th className="text-right px-4 py-3 ">Count</th>
                                                                </tr>
                                                            </thead>

                                                            <tbody>
                                                                {allServices.map((item: any, index: number) => (
                                                                    <tr key={index} className="border-t">
                                                                        <td className="px-4 py-3">{item.serviceName}</td>
                                                                        <td className="px-4 py-3 text-right">{item.quantity}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>



                                                </div>

                                            )
                                        }

                                        {
                                            positions && (
                                                <div className="mt-4 border-b pb-2">
                                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                                        Positions
                                                    </h3>
                                                    <div className="overflow-x-auto border rounded-lg">
                                                        <table className="w-full text-sm">
                                                            <thead className="bg-gray-50 text-gray-600">
                                                                <tr>
                                                                    <th className="text-left px-4 py-3">Service Type</th>
                                                                    <th className="text-left px-4 py-3">Position</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    positions?.sort((itemA: any, itemB: any) => itemA?.serviceId - itemB?.serviceId)?.map((pos: any, ind: number) => (
                                                                        <tr className="border-t">
                                                                            <td className="px-4 py-3">{servicesOptions?.filter((service: any) => service?.serviceID === pos?.serviceId)[0]?.serviceName}</td>
                                                                            <td className="px-4 py-3">{positionsNames?.filter((posName: any) => posName?.statusID === pos?.position)[0]?.statusValue}</td>

                                                                        </tr>
                                                                    ))
                                                                }

                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        {
                                            appointmentData?.vehicle && (
                                                <p className="font-semibold text-gray-700">IsCommercial: <span className="text-gray-700 font-medium">{appointmentData?.vehicle?.isCommercial ? "Yes" : "No"}</span></p>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>

                        )}

                        {/* Invoice Lines */}
                        <div className="mt-4 border-b pb-2">
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                Invoice Details
                            </h3>

                            {invoiceData?.lines?.length > 0 ? (
                                <div className="overflow-x-auto border rounded-lg">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-gray-600">
                                            <tr>
                                                <th className="text-left px-4 py-3">Type</th>
                                                <th className="text-left px-4 py-3">Description</th>
                                                <th className="text-right px-4 py-3">Qty</th>
                                                <th className="text-right px-4 py-3">Unit Price</th>
                                                <th className="text-right px-4 py-3">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceData?.lines.map((item: any, index: number) => (
                                                <tr key={index} className="border-t">
                                                    <td className="px-4 py-3">{item.type}</td>
                                                    <td className="px-4 py-3">{item.description}</td>
                                                    <td className="px-4 py-3 text-right">{item.quantity}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        ${item.unitPrice?.toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium">
                                                        ${item.lineTotal?.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 italic">
                                    No invoice items available.
                                </div>
                            )}
                        </div>

                        {/* Summary */}
                        <div className="flex justify-end mt-2 w-full">
                            <div className="w-full border rounded-lg p-4 space-y-2 text-sm">
                                <SummaryRow label="Service Total" value={invoiceData?.serviceTotal} />
                                <SummaryRow label="Product Total" value={invoiceData?.prodcutTotal} />
                                <SummaryRow label="Subtotal" value={invoiceData?.subTotal} />
                                <SummaryRow label="Tax" value={invoiceData?.taxAmount} />
                                <SummaryRow label="Total Transaction Fee" value={invoiceData?.transactionFee} />
                                <SummaryRow label="Total" value={invoiceData?.totalAmount} bold />
                                <SummaryRow label="Paid" value={invoiceData?.amountPaid} />
                                <div className="border-t pt-2">
                                    <SummaryRow
                                        label="Balance Due"
                                        value={invoiceData?.balanceDue}
                                        highlight={true}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex border-t mt-2">
                            <button
                                type="button"
                                className="w-full sm:w-auto px-4 py-2 mt-6 rounded bg-primary text-white cursor-pointer"
                                onClick={() => { goToNextPage() }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

type SummaryRowProps = {
    label: string,
    value: number,
    bold?: boolean,
    highlight?: boolean
}

const SummaryRow = ({ label, value, bold, highlight }: SummaryRowProps) => (
    <div className="flex justify-between">
        <span className={`${bold ? "font-semibold" : ""}`}>
            {label}
        </span>
        <span
            className={`
        ${bold ? "font-semibold" : ""}
        ${highlight ? "text-red-600 font-bold" : ""}
      `}
        >
            ${value?.toFixed(2) || "0.00"}
        </span>
    </div>
);